import Vue from 'vue'
import ViewAudioPip from '@views/question-review/components/AnswerFileFullScreen/src/ViewAudioPip.vue'

/**
 * 审单录音出声源单例：
 * - 唯一 <audio>（绕过 Document PiP 有声自动播放限制）
 * - Document PiP（静音 video 作时钟，控件遥控本 host）
 * 证据 / 录音大屏销毁时不要 destroy；离开审单页再 destroy。
 */

let home = null
let audio = null
let pipRoot = null
let pipActive = false
let listeners = Object.create(null)

function emit (event, payload) {
  const list = listeners[event]
  if (!list || !list.length) return
  list.slice().forEach((fn) => {
    try {
      fn(payload)
    } catch (e) {
      console.error(e)
    }
  })
}

function findLyricIndex (lyrics, timeMs) {
  if (!Array.isArray(lyrics) || !lyrics.length) return -1
  for (let i = 0; i < lyrics.length; i++) {
    const row = lyrics[i] || {}
    const bg = Number(row.bg)
    const ed = Number(row.ed)
    const next = lyrics[i + 1]
    if (!Number.isFinite(bg) || !Number.isFinite(ed)) continue
    if (next && timeMs >= ed && timeMs <= Number(next.bg)) return i
    if (timeMs >= bg && timeMs <= ed) return i
  }
  return -1
}

function getPip () {
  return pipRoot && pipRoot.$refs ? pipRoot.$refs.pip : null
}

function ensurePipRoot () {
  if (pipRoot) return pipRoot
  pipRoot = new Vue({
    data () {
      return {
        lyrics: [],
        currentLyricIndex: -1,
        audioSrc: '',
      }
    },
    methods: {
      onTimeUpdate (ms) {
        const idx = findLyricIndex(this.lyrics, ms)
        if (idx !== this.currentLyricIndex) {
          this.currentLyricIndex = idx
        }
      },
      onPipClosed (payload) {
        pipActive = false
        const parked = audio && home && audio.parentNode === home
        if (parked && payload && typeof payload.currentTime === 'number' && isFinite(payload.currentTime)) {
          try {
            audio.currentTime = payload.currentTime
          } catch (e) {}
        }
        emit('pip-closed', payload || {})
        if (parked) audio.pause()
      },
      onPipError (err) {
        pipActive = false
        emit('pip-error', err)
      },
    },
    render (h) {
      return h(ViewAudioPip, {
        ref: 'pip',
        props: {
          lyrics: this.lyrics,
          currentLyricIndex: this.currentLyricIndex,
          audioSrc: this.audioSrc,
        },
        on: {
          'time-update': this.onTimeUpdate,
          'pip-closed': this.onPipClosed,
          'pip-error': this.onPipError,
        },
      })
    },
  })
  pipRoot.$mount()
  home.appendChild(pipRoot.$el)
  return pipRoot
}

function ensure () {
  if (audio) return AudioSoundHost
  home = document.createElement('div')
  home.setAttribute('data-sj-audio-sound-host', '1')
  home.style.cssText = 'position:fixed;left:-9999px;top:0;width:0;height:0;overflow:hidden;'
  audio = document.createElement('audio')
  audio.preload = 'auto'
  home.appendChild(audio)
  document.body.appendChild(home)
  ensurePipRoot()
  return AudioSoundHost
}

const AudioSoundHost = {
  get audio () {
    ensure()
    return audio
  },

  /** 借给录音大屏挂 Plyr；大屏销毁前必须 release */
  borrow (parentEl) {
    ensure()
    if (parentEl && audio.parentNode !== parentEl) {
      parentEl.appendChild(audio)
    }
    return audio
  },

  release () {
    if (!audio || !home) return
    if (audio.parentNode !== home) {
      home.appendChild(audio)
    }
  },

  setSrc (url, options) {
    ensure()
    if (!url) return
    const next = String(url)
    ensurePipRoot().audioSrc = next
    if (audio.getAttribute('src') !== next) {
      audio.setAttribute('src', next)
      audio.load()
    }
    if (options && options.autoplay) {
      this.play()
    }
  },

  play () {
    ensure()
    const p = audio.play()
    return p && p.catch ? p.catch(() => {}) : Promise.resolve()
  },

  pause () {
    if (audio && !audio.paused) audio.pause()
  },

  seek (time) {
    if (!audio || !isFinite(time)) return
    try {
      audio.currentTime = time
    } catch (e) {}
  },

  setRate (rate) {
    const value = Number(rate)
    if (!audio || !Number.isFinite(value) || value <= 0) return
    audio.playbackRate = value
  },

  get currentTime () {
    return audio ? (audio.currentTime || 0) : 0
  },

  get paused () {
    return !audio || audio.paused
  },

  get isPipActive () {
    return !!pipActive
  },

  setPipLyrics (lyrics, lyricIndex) {
    const root = ensurePipRoot()
    root.lyrics = Array.isArray(lyrics) ? lyrics : []
    if (typeof lyricIndex === 'number') {
      root.currentLyricIndex = lyricIndex
    }
  },

  async openPip (options) {
    ensure()
    options = options || {}
    if (!window.documentPictureInPicture) return false
    if (pipActive) return true

    const pip = getPip()
    if (!pip) return false

    if (options.audioSrc) this.setSrc(options.audioSrc)
    this.setPipLyrics(options.lyrics, options.currentLyricIndex)

    const wasPlaying = options.wasPlaying != null
      ? !!options.wasPlaying
      : !audio.paused && !audio.ended
    const currentTime = typeof options.currentTime === 'number'
      ? options.currentTime
      : (audio.currentTime || 0)

    pipActive = true
    const success = await pip.initPictureInPicture({
      wasPlaying,
      currentTime,
      masterAudio: audio,
    })
    if (!success) {
      pipActive = false
      emit('pip-error', new Error('pip init failed'))
      return false
    }
    return true
  },

  closePip () {
    const pip = getPip()
    if (pip && typeof pip.teardownPipWindow === 'function') {
      pip.teardownPipWindow()
    }
  },

  on (event, handler) {
    if (typeof handler !== 'function') return
    if (!listeners[event]) listeners[event] = []
    listeners[event].push(handler)
  },

  off (event, handler) {
    const list = listeners[event]
    if (!list) return
    const i = list.indexOf(handler)
    if (i >= 0) list.splice(i, 1)
  },

  destroy () {
    try {
      this.closePip()
    } catch (e) {}
    try {
      this.pause()
    } catch (e) {}
    if (pipRoot) {
      pipRoot.$destroy()
      if (pipRoot.$el && pipRoot.$el.parentNode) {
        pipRoot.$el.parentNode.removeChild(pipRoot.$el)
      }
      pipRoot = null
    }
    if (home && home.parentNode) {
      home.parentNode.removeChild(home)
    }
    home = null
    audio = null
    pipActive = false
    listeners = Object.create(null)
  },
}

export default AudioSoundHost
export { AudioSoundHost }



// WEBPACK FOOTER //
// ./src/views/question-review/components/AudioSoundHost/index.js